import { useEffect, useState } from "react";
import { Share2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

const DISMISS_KEY = "marketlink.inviteNudge.dismissedAt";
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

interface ReferralStats {
  totalReferred: number;
  tier: string | null;
  nextTier: string | null;
  nextTierDiscount: number | null;
  nextTierReferralsAway: number | null;
}

export function InviteNudgeBanner() {
  const { isAuthenticated, user } = useAuth();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setShow(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const dismissedAt = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
        if (Date.now() - dismissedAt < COOLDOWN_MS) {
          if (!cancelled) setShow(false);
          return;
        }
        const res = await fetch("/api/my/referral-stats", { credentials: "include" });
        if (!res.ok) return;
        const data = (await res.json()) as ReferralStats;
        if (cancelled) return;
        setStats(data);
        if ((data.totalReferred ?? 0) < 3) {
          setShow(true);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  if (!show || !user) return null;

  const handleShare = async () => {
    try {
      const res = await fetch("/api/my/referral-code", { credentials: "include" });
      if (!res.ok) return;
      const data = (await res.json()) as { code: string; shareUrl: string };
      const text = `Help me bring Stoop to our block — local food from the makers near us. ${data.shareUrl}`;
      if (navigator.share) {
        await navigator.share({ title: "Stoop", text });
      } else {
        await navigator.clipboard.writeText(data.shareUrl);
      }
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
      setShow(false);
    } catch {
      /* user cancelled */
    }
  };

  const handleDismiss = () => {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const teaser =
    stats?.nextTier && stats.nextTierReferralsAway
      ? `${stats.nextTierReferralsAway} more invite${stats.nextTierReferralsAway === 1 ? "" : "s"} unlocks ${stats.nextTierDiscount}% off as ${stats.nextTier}.`
      : `Every neighbor you invite earns you 10% off and helps us launch.`;

  return (
    <div className="mx-3 my-3 rounded-xl border border-primary/30 bg-primary/5 p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="font-semibold text-foreground">Help us reach launch</div>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Stoop is in beta. We need more neighbors to graduate to live mode. {teaser}
          </p>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="rounded-full p-1 text-muted-foreground hover:bg-muted/60"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <Button onClick={handleShare} className="mt-3 w-full" size="sm">
        <Share2 className="mr-2 h-4 w-4" />
        Invite a neighbor
      </Button>
    </div>
  );
}
